import type { OrgIdsResponse, OrgListQuery, OrganisationDto, Paginated } from "@ads/shared";

/**
 * GET /api/organisations — searchable / paginated / sortable org list (§7, US-B1/B2).
 * Same-origin request (the SPA and its API share `ads.epublisherapp.com` — §8).
 */
export async function fetchOrganisations(query: OrgListQuery): Promise<Paginated<OrganisationDto>> {
  const params = new URLSearchParams({
    page: String(query.page),
    limit: String(query.limit),
    sort: query.sort,
    order: query.order,
  });
  if (query.q) params.set("q", query.q);

  const res = await fetch(`/api/organisations?${params.toString()}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`failed to load organisations: ${res.status}`);
  return (await res.json()) as Paginated<OrganisationDto>;
}

/** GET /api/organisations/:id — one org (name + custId) for the listing-page header. */
export async function fetchOrganisation(id: string): Promise<OrganisationDto> {
  const res = await fetch(`/api/organisations/${id}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`failed to load organisation: ${res.status}`);
  return (await res.json()) as OrganisationDto;
}

/**
 * GET /api/organisations/ids — every encoded id matching the (optional) search, across
 * ALL pages. Powers "Select All" so it targets the whole filtered set, not just the
 * visible page (US-B3 — the legacy bug).
 */
export async function fetchOrganisationIds(q?: string): Promise<string[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  const res = await fetch(`/api/organisations/ids?${params.toString()}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`failed to load organisation ids: ${res.status}`);
  return ((await res.json()) as OrgIdsResponse).ids;
}
